import React, { useState, useEffect, useRef, useCallback } from "react";
import { FaEdit, FaTrash, FaMoneyBillWave, FaPlus } from "react-icons/fa";
import Sidebar from "../Sidebar/Sidebar";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import "./InventoryItem.css";

const ITEMS_PER_PAGE = 20;

const emptyBatchForm = {
  batch_number: "",
  quantity: "",
  expiration_date: "",
  nafdac_number: "",
};

const emptyConvertForm = {
  batch_number: "",
  expiration_date: "",
  nafdac_number: "",
};

const InventoryItem = () => {
  const [inventory, setInventory] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [editingPrice, setEditingPrice] = useState(null);
  const [updatingPrice, setUpdatingPrice] = useState(false);
  const [userType, setUserType] = useState(null);
  const [addBatchFor, setAddBatchFor] = useState(null);
  const [batchForm, setBatchForm] = useState(emptyBatchForm);
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [convertBatchFor, setConvertBatchFor] = useState(null);
  const [convertForm, setConvertForm] = useState(emptyConvertForm);
  const [submittingConvert, setSubmittingConvert] = useState(false);

  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  const canSeeCostPrice =
    userType === "PHARMACY" || userType === "PHARMACYBRANCH";

  const displayValue = (value) =>
    value !== null && value !== undefined && value !== "" ? value : "N/A";

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    try {
      const { jwtDecode } = require("jwt-decode");
      const decoded = jwtDecode(token);
      const type = (decoded.user_type?.value || decoded.user_type || "").toString().trim();
      setUserType(type);
    } catch (err) {
      console.error("Failed to decode token for user type:", err);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchInventory = useCallback(
    async (page = 1, query = "", isInitial = false) => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      if (isInitial) setIsLoading(true);
      else setIsSearching(true);

      try {
        const params = { page, limit: ITEMS_PER_PAGE };
        if (query.trim()) params.query = query.trim();

        const { data } = await api.get("/pharmacies/get_inventory", {
          params,
          signal: abortRef.current.signal,
        });

        if (data && Array.isArray(data.data)) {
          const normalized = data.data.map((item) => ({
            ...item,
            drug_name: item.brand_name
              ? item.generic_name
                ? `${item.brand_name} (${item.generic_name})`
                : item.brand_name
              : item.generic_name || "Unnamed Drug",
          }));
          setInventory(normalized);
          setTotalItems(data.total_items || 0);
        } else {
          throw new Error("Unexpected response format.");
        }
      } catch (err) {
        if (err.name === "CanceledError" || err.name === "AbortError") return;
        setError("Failed to load inventory. Please try again later.");
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    },
    []
  );

  useEffect(() => { fetchInventory(1, "", true); }, [fetchInventory]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchInventory(1, value, false), 400);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchInventory(newPage, searchTerm, false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => { setSuccess(""); setError(""); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const handleEdit = (item) => {
    if (item.batches && item.batches.length > 0) {
      setError("Cannot edit inventory with batches. You can only update the price.");
      return;
    }
    navigate("/UpdateInventoryManually", { state: { drugFromDashboard: item } });
  };

  const handlePriceEdit = (item) =>
    setEditingPrice({ id: item.id, price: item.price || 0 });

  const handlePriceUpdate = async (itemId) => {
    if (!editingPrice || editingPrice.id !== itemId) return;
    const newPrice = parseFloat(editingPrice.price);
    if (isNaN(newPrice) || newPrice < 0) { setError("Please enter a valid price."); return; }
    setUpdatingPrice(true);
    try {
      await api.put("/pharmacies/update_inventory", { id: itemId, price: newPrice });
      setInventory((prev) =>
        prev.map((item) => item.id === itemId ? { ...item, price: newPrice } : item)
      );
      setSuccess("Price updated successfully!");
      setEditingPrice(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update price.");
    } finally {
      setUpdatingPrice(false);
    }
  };

  const handlePriceCancel = () => setEditingPrice(null);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this inventory item?")) return;
    try {
      await api.delete(`/pharmacies/inventory/${id}`);
      setInventory((prev) => prev.filter((item) => item.id !== id));
      setTotalItems((prev) => prev - 1);
      setSuccess("Inventory item deleted successfully.");
    } catch (err) {
      setError("Failed to delete inventory item.");
    }
  };

  const handleBatchEdit = (batch, parent) => {
    // inventory_id is required by PUT /pharmacies/batches/{batch_number} —
    // batch_number alone isn't globally unique, only unique per inventory
    // (see uq_unique_batch_per_inventory). batch objects from get_inventory's
    // BatchResponse never carry their parent inventory's id on their own,
    // so it's threaded through here explicitly from `parent` (the inventory
    // item being iterated), nested inside batchData so UpdateBatch.jsx can
    // read it the same way it reads every other batch field.
    navigate("/UpdateBatch", {
      state: { batchData: { ...batch, inventory_id: parent.id } },
    });
  };

  const handleBatchDelete = async (batchNumberOrId, parentId) => {
    if (!window.confirm("Are you sure you want to delete this batch?")) return;
    try {
      await api.delete(`/pharmacies/batch/${batchNumberOrId}`);
      setInventory((prev) =>
        prev.map((item) =>
          item.id === parentId
            ? { ...item, batches: item.batches.filter(
                (b) => b.batch_number !== batchNumberOrId && b.id !== batchNumberOrId
              )}
            : item
        )
      );
      setSuccess("Batch deleted successfully.");
    } catch (err) {
      setError("Failed to delete batch.");
    }
  };

  const openAddBatch = (item) => {
    const activeBatches = (item.batches || []).filter((b) => !b.is_deleted);
    const needsConversion = activeBatches.length === 0 && item.quantity > 0;
    setAddBatchFor(null);
    setConvertBatchFor(null);
    setError("");
    if (needsConversion) {
      setConvertBatchFor(item.id);
      setConvertForm({ batch_number: "", expiration_date: item.expiration_date || "", nafdac_number: item.nafdac_number || "" });
    } else {
      setAddBatchFor(item.id);
      setBatchForm(emptyBatchForm);
    }
  };

  const closeAddBatch = () => { setAddBatchFor(null); setBatchForm(emptyBatchForm); };
  const closeConvertBatch = () => { setConvertBatchFor(null); setConvertForm(emptyConvertForm); };
  const handleBatchFormChange = (field, value) => setBatchForm((prev) => ({ ...prev, [field]: value }));
  const handleConvertFormChange = (field, value) => setConvertForm((prev) => ({ ...prev, [field]: value }));

  const handleConvertSubmit = async (item) => {
    if (!convertForm.batch_number.trim()) { setError("Batch number is required to convert existing stock."); return; }
    setSubmittingConvert(true);
    setError("");
    try {
      const { data } = await api.post("/pharmacies/batches/convert-existing", {
        inventory_id: item.id,
        batch_number: convertForm.batch_number.trim(),
        expiration_date: convertForm.expiration_date || null,
        nafdac_number: convertForm.nafdac_number.trim() || null,
      });
      setInventory((prev) => prev.map((inv) => inv.id !== item.id ? inv : { ...inv, batches: [data] }));
      setSuccess("Existing stock converted to batch successfully!");
      closeConvertBatch();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : "Failed to convert stock to batch.");
    } finally {
      setSubmittingConvert(false);
    }
  };

  const handleAddBatchSubmit = async (inventoryId) => {
    if (!batchForm.batch_number.trim()) { setError("Batch number is required."); return; }
    if (!batchForm.quantity || isNaN(batchForm.quantity) || Number(batchForm.quantity) <= 0) { setError("Quantity is required and must be greater than 0."); return; }
    setSubmittingBatch(true);
    setError("");
    try {
      const { data } = await api.post("/pharmacies/batches", {
        inventory_id: inventoryId,
        quantity: Number(batchForm.quantity),
        batch_number: batchForm.batch_number.trim(),
        expiration_date: batchForm.expiration_date || null,
        nafdac_number: batchForm.nafdac_number.trim() || null,
      });
      setInventory((prev) =>
        prev.map((item) => {
          if (item.id !== inventoryId) return item;
          const updatedBatches = [...(item.batches || []), data];
          return { ...item, batches: updatedBatches, quantity: updatedBatches.reduce((sum, b) => sum + (b.quantity || 0), 0) };
        })
      );
      setSuccess("Batch added successfully!");
      closeAddBatch();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : "Failed to add batch.");
    } finally {
      setSubmittingBatch(false);
    }
  };

  const toggleExpand = (id) =>
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  if (isLoading) {
    return (
      <div className="inventory-page-container">
        {!isMobile && <Sidebar />}
        <section className="section inventory-section">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading inventory...</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="inventory-page-container">
      {!isMobile && <Sidebar />}
      <section className="section inventory-section">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="inventory-header">
          <div className="search-wrapper">
            <input
              type="text"
              placeholder="Search inventory by name..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
            {isSearching && <span className="search-spinner" title="Searching...">⏳</span>}
          </div>
          <span className="inventory-total">
            {searchTerm
              ? `${totalItems} result${totalItems !== 1 ? "s" : ""} for "${searchTerm}"`
              : `Total: ${totalItems} items`}
          </span>
        </div>

        <ul className="inventory-list">
          {inventory.length === 0 ? (
            <li className="no-items">
              {searchTerm ? `No results found for "${searchTerm}"` : "No inventory items found."}
            </li>
          ) : (
            inventory.map((item) => {
              const hasManyBatches = item.batches && item.batches.length > 5;
              const isExpanded = expandedItems[item.id];
              const visibleBatches = hasManyBatches && !isExpanded ? item.batches.slice(0, 5) : item.batches || [];
              const hasBatches = item.batches && item.batches.length > 0;
              const isEditingThisPrice = editingPrice?.id === item.id;
              const isAddingBatchHere = addBatchFor === item.id;
              const isConvertingHere = convertBatchFor === item.id;
              const isAnyFormOpenHere = isAddingBatchHere || isConvertingHere;

              return (
                <li key={item.id} className="inventory-item">
                  <div className="item-info">
                    <strong>{displayValue(item.drug_name)}</strong>

                    {canSeeCostPrice && item.cost_price == null && (
                      <span style={{ fontSize: "0.72rem", background: "#fff3cd", color: "#856404", border: "1px solid #ffc107", borderRadius: "4px", padding: "1px 6px", marginLeft: "8px", fontWeight: 600, verticalAlign: "middle" }}>
                        ⚠ No cost price
                      </span>
                    )}

                    <div className="item-details-row">
                      <span>Qty: {displayValue(item.quantity)}</span>
                      <span className="price-section">
                        {isEditingThisPrice ? (
                          <span className="price-edit-inline">
                            <span className="naira-symbol">₦</span>
                            <input
                              type="number"
                              value={editingPrice.price}
                              onChange={(e) => setEditingPrice({ ...editingPrice, price: e.target.value })}
                              className="price-input-inline"
                              autoFocus min="0" step="0.01"
                              disabled={updatingPrice}
                            />
                            <button className="price-save-btn" onClick={() => handlePriceUpdate(item.id)} disabled={updatingPrice} title="Save price">
                              {updatingPrice ? "..." : "✓"}
                            </button>
                            <button className="price-cancel-btn" onClick={handlePriceCancel} disabled={updatingPrice} title="Cancel">✕</button>
                          </span>
                        ) : (
                          <>
                            | ₦{displayValue(item.price)}
                            {hasBatches && (
                              <button className="inline-price-edit-icon" onClick={() => handlePriceEdit(item)} title="Edit price">
                                <FaEdit />
                              </button>
                            )}
                          </>
                        )}
                      </span>
                      {!hasBatches && <span>| Exp: {displayValue(item.expiration_date)}</span>}
                    </div>

                    <div>
                      Form: {displayValue(item.dosage_form)} | Strength: {displayValue(item.strength)} | Cat: {displayValue(item.category)}
                    </div>

                    {/* ── Cost & Wholesale price row — owner and branch manager only ── */}
                    {canSeeCostPrice && (
                      <div style={{ fontSize: "0.82rem", marginTop: "3px", color: item.cost_price != null ? "#e0e0e0" : "#888" }}>
                        {item.cost_price != null ? (
                          <>
                            Cost: ₦{item.cost_price}
                            {item.price != null && (
                              <span style={{ marginLeft: "8px", color: item.price - item.cost_price >= 0 ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                                · Retail margin: ₦{(item.price - item.cost_price).toFixed(2)}
                                {item.price > 0 ? ` (${(((item.price - item.cost_price) / item.price) * 100).toFixed(1)}%)` : ""}
                              </span>
                            )}
                            {/* ── Wholesale price display ── */}
                            {item.wholesale_price != null && (
                              <span style={{ marginLeft: "8px", color: "#64b5f6", fontWeight: 500 }}>
                                | Wholesale: ₦{item.wholesale_price}
                                <span style={{ marginLeft: "6px", color: item.wholesale_price - item.cost_price >= 0 ? "#1565c0" : "#c62828", fontWeight: 600 }}>
                                  · margin: ₦{(item.wholesale_price - item.cost_price).toFixed(2)}
                                  {item.wholesale_price > 0 ? ` (${(((item.wholesale_price - item.cost_price) / item.wholesale_price) * 100).toFixed(1)}%)` : ""}
                                </span>
                              </span>
                            )}
                          </>
                        ) : (
                          <>
                            <span style={{ fontStyle: "italic" }}>Cost price not set — profit won't be tracked in POS</span>
                            {/* Still show wholesale price even if no cost price */}
                            {item.wholesale_price != null && (
                              <span style={{ marginLeft: "8px", color: "#64b5f6", fontWeight: 500 }}>
                                | Wholesale: ₦{item.wholesale_price}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* ── Wholesale price visible to all staff (not just owner) ── */}
                    {!canSeeCostPrice && item.wholesale_price != null && (
                      <div style={{ fontSize: "0.82rem", marginTop: "3px", color: "#64b5f6" }}>
                        Wholesale: ₦{item.wholesale_price}
                      </div>
                    )}
                  </div>

                  <div className="item-actions">
                    {hasBatches ? (
                      <button className="price-edit-btn" onClick={() => handlePriceEdit(item)} title="Update price for all batches" disabled={isEditingThisPrice}>
                        <FaMoneyBillWave /> Update Price
                      </button>
                    ) : (
                      <button className="edit-btn" onClick={() => handleEdit(item)} title="Edit this inventory">
                        <FaEdit /> Edit
                      </button>
                    )}

                    <button
                      className="add-batch-btn"
                      onClick={() => {
                        if (isAddingBatchHere) return closeAddBatch();
                        if (isConvertingHere) return closeConvertBatch();
                        openAddBatch(item);
                      }}
                    >
                      <FaPlus /> {isAnyFormOpenHere ? "Cancel" : "Add Batch"}
                    </button>

                    <button className="delete-btn" onClick={() => handleDelete(item.id)} title="Delete this inventory">
                      <FaTrash /> Delete
                    </button>
                  </div>

                  {/* ── Convert existing stock form ── */}
                  {isConvertingHere && (
                    <div className="add-batch-form convert-batch-form">
                      <h4 className="add-batch-title">⚠️ Assign Existing Stock to a Batch</h4>
                      <p className="convert-batch-note">
                        This inventory has <strong>{item.quantity} units</strong> not assigned to any batch. Enter the real batch number for this existing stock before adding new batches.
                      </p>
                      <div className="add-batch-fields">
                        <input type="text" placeholder="Batch Number *" value={convertForm.batch_number} onChange={(e) => handleConvertFormChange("batch_number", e.target.value)} disabled={submittingConvert} />
                        <input type="date" value={convertForm.expiration_date} onChange={(e) => handleConvertFormChange("expiration_date", e.target.value)} disabled={submittingConvert} />
                        <input type="text" placeholder="NAFDAC Number (optional)" value={convertForm.nafdac_number} onChange={(e) => handleConvertFormChange("nafdac_number", e.target.value)} disabled={submittingConvert} />
                      </div>
                      <div className="add-batch-form-actions">
                        <button className="batch-submit-btn" onClick={() => handleConvertSubmit(item)} disabled={submittingConvert}>
                          {submittingConvert ? <><span className="button-spinner"></span> Converting...</> : "Convert to Batch"}
                        </button>
                        <button className="batch-cancel-btn" onClick={closeConvertBatch} disabled={submittingConvert}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* ── Normal add batch form ── */}
                  {isAddingBatchHere && (
                    <div className="add-batch-form">
                      <h4 className="add-batch-title">➕ New Batch</h4>
                      <div className="add-batch-fields">
                        <input type="text" placeholder="Batch Number *" value={batchForm.batch_number} onChange={(e) => handleBatchFormChange("batch_number", e.target.value)} disabled={submittingBatch} />
                        <input type="number" placeholder="Quantity *" value={batchForm.quantity} onChange={(e) => handleBatchFormChange("quantity", e.target.value)} disabled={submittingBatch} min="1" />
                        <input type="date" value={batchForm.expiration_date} onChange={(e) => handleBatchFormChange("expiration_date", e.target.value)} disabled={submittingBatch} />
                        <input type="text" placeholder="NAFDAC Number (optional)" value={batchForm.nafdac_number} onChange={(e) => handleBatchFormChange("nafdac_number", e.target.value)} disabled={submittingBatch} />
                      </div>
                      <div className="add-batch-form-actions">
                        <button className="batch-submit-btn" onClick={() => handleAddBatchSubmit(item.id)} disabled={submittingBatch}>
                          {submittingBatch ? <><span className="button-spinner"></span> Saving...</> : "Save Batch"}
                        </button>
                        <button className="batch-cancel-btn" onClick={closeAddBatch} disabled={submittingBatch}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {visibleBatches.length > 0 && (
                    <div className="batch-list">
                      {hasBatches && (
                        <div className="batch-price-note">
                          💡 All batches share the same price: ₦{item.price}
                        </div>
                      )}
                      {visibleBatches.map((batch) => (
                        <div key={batch.id} className="batch-item styled-batch">
                          <div className="batch-info">
                            Batch: {displayValue(batch.batch_number)} | Qty: {displayValue(batch.quantity)} | Exp: {displayValue(batch.expiration_date)}
                          </div>
                          <div className="batch-actions">
                            <button className="batch-edit-btn" onClick={() => handleBatchEdit(batch, item)}><FaEdit /> Edit</button>
                            <button className="batch-delete-btn" onClick={() => handleBatchDelete(batch.batch_number || batch.id, item.id)}><FaTrash /> Delete</button>
                          </div>
                        </div>
                      ))}
                      {hasManyBatches && (
                        <button className="toggle-batches-btn" onClick={() => toggleExpand(item.id)}>
                          {isExpanded ? "Show Less" : `Show All (${item.batches.length})`}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })
          )}
        </ul>

        {totalPages > 1 && (
          <div className="pagination">
            <button disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>Prev</button>
            <span>Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
          </div>
        )}
      </section>
    </div>
  );
};

export default InventoryItem;